/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



// @mui material components
import Divider from "@mui/material/Divider";
import Switch from "@mui/material/Switch";
import Box from "components/generals/Box";
import MDBox from "dashboard-template/components/MDBox";
import DefaultDashboardLayout from "layouts/dashboard/DefaultDashboardLayout";
import Card from "@mui/material/Card";
import MDButton from "dashboard-template/components/MDButton";
import Typography from "components/generals/Typography";
import SideNavarColor from "components/generals/SideNavarColor";

import {
  sidenavTypeButtonsStyles,
  sidenavTypeActiveButtonStyles,
} from "styles/components/sidenavStyles";

// Material Dashboard 2 PRO React context
import {
  useMaterialUIController,
  setTransparentSidenav,
  setWhiteSidenav,
  setMiniSidenav,
  setFixedNavbar,
  setDarkMode,
  setLanguage,
} from "context/ThemeContext";
import { FormControl, MenuItem, Select, SelectChangeEvent } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import i18n from "i18n";
import { useDispatch, useSelector } from "react-redux";
import { authAction } from "store/store";
import { UserInterface } from "@malmur-technologies/medicalsystem-shared/dist/interfaces/user.interface";
import { CoreAPI_URLs } from "constants/http.const";
import useHttp from "hooks/http.hook";
import { HeaderEnum } from "@malmur-technologies/medicalsystem-shared/dist/enums/header.enum";
import { PersonInterface } from "@malmur-technologies/medicalsystem-shared";
import { LanguageEnum } from "@malmur-technologies/medicalsystem-shared";
import UserForm from "components/forms/UserForm";
import { FormActionType } from "types/form.enum";
import HeaderPersonInformation from "components/views/HeaderPersonInformation";
import { EntityPermissions, PermissionsUser } from "types";
import PermissionWrapper from "components/generals/PermissionWrapper";
import SwaggerButton from "components/generals/SwaggerButton";
import { selectGeneralSettings } from "store/slices/generalSettingsSlice";

/**
 * User profile and application settings page.
 * 
 * Features:
 * - User profile information display and editing
 * - Theme customization:
 *   - Dark/light mode toggle
 *   - Sidebar color selection
 *   - Sidebar transparency options (transparent, white, dark)
 *   - Fixed/floating navbar toggle
 *   - Mini/expanded sidebar toggle
 * - Language selection (English, Spanish, Chinese, French, Italian, Portuguese)
 * - Personal information update (name, contact, password)
 * - Role and permission display
 * - Swagger API documentation access button (for admins)
 * 
 * Theme settings are persisted via Material UI context and synchronized
 * with the global application theme.
 * 
 * @returns User profile and settings page component
 */
const UserProfilePage = () => {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, fixedNavbar, transparentSidenav, whiteSidenav, darkMode } = controller;
  const [disabled, setDisabled] = useState<boolean>(false);
  const reduxDispatch = useDispatch();
  const [_isLoading, _error, sendRequest] = useHttp();
  const [userInfo, setUserInfo] = useState<UserInterface | null>(null);
  const [personInfo, setPersonInfo] = useState<PersonInterface>();
  const { t } = useTranslation();
  const generalSettings = useSelector(selectGeneralSettings);

  const savePreferenceToProfile = async (key: string, value: any) => {
    const userString = localStorage.getItem("user");
    if (!userString) return;
    const storedUser = JSON.parse(userString);

    const updatedProfile = {
      ...(storedUser.profile || {}),
      [key]: value,
    };

    const updatedUser = {
      ...storedUser,
      profile: updatedProfile,
    };

    try {
      await sendRequest<UserInterface>(
        CoreAPI_URLs.USERS_GENERAL_OPERATIONS,
        (response) => {
          if (response) {
            reduxDispatch(authAction.storeUser(response));
          }
        },
        'PUT',
        JSON.stringify(updatedUser),
        [{ header: HeaderEnum.USER_ID, value: storedUser.id }]
      );
    } catch (error) {
      console.error("Error saving user preference:", error);
    }
  };

  useEffect(() => {
    function handleDisabled() {
      return window.innerWidth > 1200 ? setDisabled(false) : setDisabled(true);
    }
    window.addEventListener("resize", handleDisabled);
    handleDisabled();
    return () => window.removeEventListener("resize", handleDisabled);
  }, []);

  /**
   * Sets sidebar to transparent style.
   * Disables white sidebar when transparent is enabled.
   * 
   * @returns {void}
   */
  const handleTransparentSidenav = async () => {
    setTransparentSidenav(dispatch, true);
    setWhiteSidenav(dispatch, false);
    await savePreferenceToProfile('transparentSidenav', true);
    await savePreferenceToProfile('whiteSidenav', false);
  };

  /**
   * Sets sidebar to white style.
   * Disables transparent sidebar when white is enabled.
   * 
   * @returns {void}
   */
  const handleWhiteSidenav = async () => {
    setWhiteSidenav(dispatch, true);
    setTransparentSidenav(dispatch, false);
    await savePreferenceToProfile('whiteSidenav', true);
    await savePreferenceToProfile('transparentSidenav', false);
  };

  /**
   * Sets sidebar to dark style.
   * Disables both transparent and white styles.
   * 
   * @returns {void}
   */
  const handleDarkSidenav = async () => {
    setWhiteSidenav(dispatch, false);
    setTransparentSidenav(dispatch, false);
    await savePreferenceToProfile('whiteSidenav', false);
    await savePreferenceToProfile('transparentSidenav', false);
  };

  /**
   * Toggles sidebar between mini (collapsed) and expanded states.
   * 
   * @returns {void}
   */
  const handleMiniSidenav = async () => {
    const newMiniSidenav = !miniSidenav;
    setMiniSidenav(dispatch, newMiniSidenav);
    await savePreferenceToProfile('miniSidenav', newMiniSidenav);
  };

  /**
   * Toggles navbar between fixed and floating positions.
   * 
   * @returns {void}
   */
  const handleFixedNavbar = async () => {
    const newFixedNavbar = !fixedNavbar;
    setFixedNavbar(dispatch, newFixedNavbar);
    await savePreferenceToProfile('fixedNavbar', newFixedNavbar);
  };

  /**
   * Toggles between dark mode and light mode themes.
   * 
   * @returns {void}
   */
  const handleDarkMode = async () => {
    const newDarkMode = !darkMode;
    setDarkMode(dispatch, newDarkMode);
    await savePreferenceToProfile('darkMode', newDarkMode);
  };

  const [lng, setLng] = React.useState("en");

  /**
   * Handles language selection change.
   * Updates application language via i18n and persists to context.
   * 
   * Supported languages: en, es, zh, fr, it, pt
   * 
   * @param {SelectChangeEvent<string>} event - Language selection event
   * @returns {void}
   */
  const handleLanguageChange = async (event: SelectChangeEvent<string>) => {
    const newLanguage = event.target.value as LanguageEnum;
    setLanguage(dispatch, newLanguage as "en" | "es" | "zh" | "fr" | "it" | "pt");
    setLng(newLanguage);
    await savePreferenceToProfile('language', newLanguage);
  };

  useEffect(() => {
    setLng(i18n.language);
  }, [i18n.language]);

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      const user = JSON.parse(storedUser);
      const userLanguagePreference = user?.profile?.language;

      // If user hasn't set a language preference, use system default
      if (!userLanguagePreference && generalSettings.language) {
        const systemLanguage = generalSettings.language as "en" | "es" | "zh" | "fr" | "it" | "pt";
        setLanguage(dispatch, systemLanguage);
        i18n.changeLanguage(systemLanguage);
      }
    }
  }, [generalSettings.language, dispatch]);

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("user"));
    onUserHandler(storedUser.id);
  }, []);

  /**
   * Fetches user and person information from API.
   * Retrieves user data by ID, then fetches associated person data.
   * 
   * @param {string} storedUserId - User ID from local storage
   * @returns {Promise<void>}
   */
  const onUserHandler = async (storedUserId: string) => {
    if (storedUserId) {
      await sendRequest<UserInterface>(
        CoreAPI_URLs.USERS_RETRIEVE_DELETE,
        async (response) => {
          if (response) {
            await sendRequest<PersonInterface>(
              CoreAPI_URLs.PERSONS_GENERAL_OPERATIONS + "/by-user/id",
              (response) => {
                if (response) {
                  setPersonInfo(response);
                }
              },
              "GET",
              JSON.stringify(response.id),
              [{ header: HeaderEnum.RESOURCE_ID, value: response.id }]
            );
            setUserInfo(response);
          }
        },
        "GET",
        JSON.stringify(storedUserId),
        [{ header: HeaderEnum.USER_ID, value: storedUserId }]
      );
    }
  };

  return (
    <DefaultDashboardLayout>
      <Box borderRadius="lg" ml={2} mt={2} width="calc(100% - 2rem)" height="calc(100% - 2rem)">
        {personInfo ? (
          <>
            <HeaderPersonInformation personInformation={personInfo} showPhoto/>
          </>
        ) : (
          <Box className="full-form-sub-wrapper"></Box>
        )}

        {userInfo && (
          <>
            <Divider />
            <Box className="full-form-sub-wrapper">
              <UserForm model={userInfo} action={FormActionType.MODIFY} person={personInfo} />
            </Box>
          </>
        )}
        <Divider />

        <Card>
          <MDBox p={2}>
            <Typography variant="h5">{t("userProfilePage.dashboardConfigurator")}</Typography>
            <MDBox pt={0.5}>
              <MDBox>
                <Typography variant="h6">{t("userProfilePage.sidenavColors")}</Typography>
                <SideNavarColor />
              </MDBox>
              <MDBox mt={3} lineHeight={1}>
                <Typography variant="h6">{t("userProfilePage.sidenavType")}</Typography>
                <Typography variant="button" color="text">
                  {t("userProfilePage.chooseBetweenDifferentSidenavTypes")}
                </Typography>
                <MDBox
                  sx={{
                    display: { xs: "flex", sm: "-webkit-box" },
                    flexDirection: { xs: "column", sm: "row" },
                    mt: 2,
                    mr: 1,
                  }}
                >
                  <MDButton
                    color="dark"
                    variant="gradient"
                    onClick={handleDarkSidenav}
                    disabled={disabled}
                    fullWidth
                    sx={
                      !transparentSidenav && !whiteSidenav
                        ? sidenavTypeActiveButtonStyles
                        : sidenavTypeButtonsStyles
                    }
                  >
                    {t("userProfilePage.dark")}
                  </MDButton>
                  <MDBox
                    sx={{
                      my: { xs: 1, sm: 0 },
                      mx: { sm: 1 },
                      sm: { width: "8rem", minWidth: "8rem" },
                    }}
                  >
                    <MDButton
                      color="dark"
                      variant="gradient"
                      onClick={handleTransparentSidenav}
                      disabled={disabled}
                      fullWidth
                      sx={
                        transparentSidenav && !whiteSidenav
                          ? sidenavTypeActiveButtonStyles
                          : sidenavTypeButtonsStyles
                      }
                    >
                      {t("userProfilePage.transparent")}
                    </MDButton>
                  </MDBox>
                  <MDButton
                    color="dark"
                    variant="gradient"
                    onClick={handleWhiteSidenav}
                    disabled={disabled}
                    fullWidth
                    sx={
                      whiteSidenav && !transparentSidenav
                        ? sidenavTypeActiveButtonStyles
                        : sidenavTypeButtonsStyles
                    }
                  >
                    {t("userProfilePage.white")}
                  </MDButton>
                </MDBox>
              </MDBox>
              <MDBox
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mt={3}
                lineHeight={1}
              >
                <Typography variant="h6">{t("userProfilePage.navbarFixed")}</Typography>

                <Switch checked={fixedNavbar} onChange={handleFixedNavbar} />
              </MDBox>
              <Divider />
              <MDBox
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                lineHeight={1}
              >
                <Typography variant="h6">{t("userProfilePage.sidenavMini")}</Typography>

                <Switch checked={miniSidenav} onChange={handleMiniSidenav} />
              </MDBox>
              <Divider />
              <MDBox
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                lineHeight={1}
              >
                <Typography variant="h6">{t("userProfilePage.lightDark")}</Typography>

                <Switch checked={darkMode} onChange={handleDarkMode} />
              </MDBox>
              <Divider />
              <MDBox>
                <Typography variant="h6">{t("userProfilePage.language")}</Typography>
              </MDBox>
              <MDBox
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                lineHeight={1}
              >
                <FormControl>
                  <Select
                    labelId="labelLanguage"
                    id="selectLanguage"
                    value={lng}
                    onChange={handleLanguageChange}
                    label="language"
                    autoWidth
                  >
                    <MenuItem value="">
                      <em>{t("userProfilePage.selectLanguage")}</em>
                    </MenuItem>
                    <MenuItem value={LanguageEnum.ENGLISH}>{t("userProfilePage.english")}</MenuItem>
                    <MenuItem value={LanguageEnum.SPANISH}>{t("userProfilePage.spanish")}</MenuItem>
                    <MenuItem value={LanguageEnum.FRENCH}>{t("userProfilePage.french")}</MenuItem>
                    <MenuItem value={LanguageEnum.ITALIAN}>{t("userProfilePage.italian")}</MenuItem>
                    <MenuItem value={LanguageEnum.PORTUGUESE}>{t("userProfilePage.portuguese")}</MenuItem>
                  </Select>
                </FormControl>
              </MDBox>
              <Divider />
              <PermissionWrapper
                requiredPermissions={[PermissionsUser[EntityPermissions.APPOINTMENTS].CREATE]}
              >
                <SwaggerButton variant="outlined" color="info" size="small" />
              </PermissionWrapper>
            </MDBox>
          </MDBox>
        </Card>
      </Box>
    </DefaultDashboardLayout>
  );
};

export default UserProfilePage;

